import { useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { TooltipProps } from 'recharts'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'
import { cn, formatMoney, formatNumber } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/search-select'
import { FoldToggle } from '@/features/projects/fold-toggle'
import { AXIS_TICK, compactMoney, GRID_COLOR } from '@/features/home/model'

type Bucket = components['schemas']['LLMCostBucket']
type Option = components['schemas']['LLMCostOption']
type DayPoint = components['schemas']['LLMCostTimeseriesPoint']

interface Filters {
  start: string // YYYY-MM-DD
  end: string
  feature: string | undefined
  model: string | undefined
}

const PRESETS = [7, 30, 90] as const
// Categorical slots (dataviz reference palette). A feature keeps its color whatever the dates: the usual
// largest features own a slot, and any other feature in a range's top five takes the first free one.
const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948']
const OWNED_SLOTS = [
  'product_structured_refinement',
  'product_configuration_check',
  'product_media_seedvr2_upscale',
  'po_email_analysis',
  'accounting_invoice_email_analysis',
]
const SERIES_COUNT = 5
const OTHER_COLOR = '#a3a3a3'
const OTHER_KEY = '__other__'
const LIST_COUNT = 8
// Features listed under each model; the rest are summed into one line (picking the model lists them all).
const PART_COUNT = 3

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function lastDays(days: number): Pick<Filters, 'start' | 'end'> {
  const end = new Date()
  const start = new Date(end)
  start.setDate(end.getDate() - (days - 1))
  return { start: isoDay(start), end: isoDay(end) }
}

function presetOf(f: Filters): number | undefined {
  return PRESETS.find((days) => {
    const range = lastDays(days)
    return range.start === f.start && range.end === f.end
  })
}

function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function useCostQuery<T>(path: '/dashboard/ai-costs/summary' | '/dashboard/ai-costs/timeseries', f: Filters) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['ai-costs', path, f],
    // Keep showing the last numbers while new dates or filters load.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const client = await getClient()
      const query = { start: f.start, end: f.end, feature: f.feature, model: f.model }
      const { data, error } =
        path === '/dashboard/ai-costs/summary'
          ? await client.GET(path, { params: { query: { ...query, top: 50 } } })
          : await client.GET(path, { params: { query } })
      if (error) throw error
      return data as T
    },
  })
}

function useOptions() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['ai-costs', 'options'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const client = await getClient()
      const [features, models] = await Promise.all([
        client.GET('/dashboard/ai-costs/features'),
        client.GET('/dashboard/ai-costs/models'),
      ])
      return { features: features.data ?? [], models: models.data ?? [] }
    },
  })
}

export function AICostsPage() {
  const [filters, setFilters] = useState<Filters>(() => ({
    ...lastDays(30),
    feature: undefined,
    model: undefined,
  }))
  const summary = useCostQuery<components['schemas']['LLMCostSummary']>('/dashboard/ai-costs/summary', filters)
  const timeseries = useCostQuery<DayPoint[]>('/dashboard/ai-costs/timeseries', filters)
  const options = useOptions()
  const total = summary.data
  const byFeature = total?.by_feature ?? []
  const featureLabel = (key: string) =>
    options.data?.features.find((option) => option.key === key)?.label ??
    byFeature.find((bucket) => bucket.key === key)?.label ??
    key

  const toggle = (field: 'feature' | 'model', key: string) =>
    setFilters((current) => ({ ...current, [field]: current[field] === key ? undefined : key }))

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <h1 className="text-2xl font-semibold">AI costs</h1>
        <FilterBar filters={filters} options={options.data} onChange={setFilters} />
      </header>

      <div
        aria-busy={summary.isFetching || timeseries.isFetching}
        className={cn(
          'space-y-6 transition-opacity',
          (summary.isPlaceholderData || timeseries.isPlaceholderData) && 'opacity-60',
        )}
      >
        <Card className="py-4">
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 xl:grid-cols-4">
              <Figure label="Spend" value={formatMoney(total?.total_cost_usd)} strong />
              <Figure label="Calls" value={formatNumber(total?.total_calls, 0)} />
              <Figure label="Input tokens" value={formatNumber(total?.total_input_tokens, 0)} />
              <Figure label="Output tokens" value={formatNumber(total?.total_output_tokens, 0)} />
            </dl>
          </CardContent>
        </Card>

        <DailySpend points={timeseries.data ?? []} ranked={byFeature} featureLabel={featureLabel} />

        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
          <BreakdownCard
            className="lg:col-span-2"
            title="By feature"
            noun="features"
            rows={byFeature}
            selected={filters.feature}
            onSelect={(key) => toggle('feature', key)}
          />
          <div className="grid gap-4">
            <BreakdownCard
              title="By model"
              noun="models"
              rows={total?.by_model ?? []}
              selected={filters.model}
              onSelect={(key) => toggle('model', key)}
            />
            <BreakdownCard title="By person" noun="people" rows={total?.by_user ?? []} />
          </div>
        </div>
      </div>
    </div>
  )
}

function FilterBar({
  filters,
  options,
  onChange,
}: {
  filters: Filters
  options: { features: Option[]; models: Option[] } | undefined
  onChange: (next: Filters) => void
}) {
  const preset = presetOf(filters)
  const filtered = filters.feature != null || filters.model != null
  return (
    <div className="flex w-full flex-wrap items-end gap-3 xl:w-auto">
      <div className="w-full space-y-1 sm:w-auto">
        <Label className="text-xs">Dates</Label>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border p-0.5" role="group" aria-label="Date range">
            {PRESETS.map((days) => (
              <button
                key={days}
                type="button"
                aria-pressed={preset === days}
                onClick={() => onChange({ ...filters, ...lastDays(days) })}
                className={cn(
                  'h-7 rounded px-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                  preset === days
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {days} days
              </button>
            ))}
          </div>
          <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:flex sm:w-auto">
            <Input
              type="date"
              aria-label="From"
              value={filters.start}
              max={filters.end}
              onChange={(e) => e.target.value && onChange({ ...filters, start: e.target.value })}
              className="h-8 w-full min-w-0 px-2 text-sm sm:w-[150px]"
            />
            <span className="text-sm text-muted-foreground">to</span>
            <Input
              type="date"
              aria-label="To"
              value={filters.end}
              min={filters.start}
              onChange={(e) => e.target.value && onChange({ ...filters, end: e.target.value })}
              className="h-8 w-full min-w-0 px-2 text-sm sm:w-[150px]"
            />
          </div>
        </div>
      </div>
      <OptionSelect
        label="Feature"
        allLabel="All features"
        value={filters.feature}
        options={options?.features ?? []}
        onChange={(feature) => onChange({ ...filters, feature })}
      />
      <OptionSelect
        label="Model"
        allLabel="All models"
        value={filters.model}
        options={options?.models ?? []}
        onChange={(model) => onChange({ ...filters, model })}
      />
      {filtered ? (
        <Button variant="ghost" size="sm" onClick={() => onChange({ ...filters, feature: undefined, model: undefined })}>
          <X /> Clear
        </Button>
      ) : null}
    </div>
  )
}

function OptionSelect({
  label,
  allLabel,
  value,
  options,
  onChange,
}: {
  label: string
  allLabel: string
  value: string | undefined
  options: Option[]
  onChange: (value: string | undefined) => void
}) {
  return (
    <div className="w-full space-y-1 sm:w-auto">
      <Label className="text-xs">{label}</Label>
      <SearchSelect
        ariaLabel={label}
        allLabel={allLabel}
        searchPlaceholder={`Search ${label.toLowerCase()}s`}
        value={value}
        options={options.map((option) => ({ value: option.key, label: option.label, description: option.description }))}
        onChange={onChange}
        className="w-full sm:w-[210px]"
      />
    </div>
  )
}

function Figure({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 tabular-nums',
          strong ? 'text-xl font-semibold sm:text-2xl' : 'text-base font-medium sm:text-xl',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

type ChartRow = { date: string; label: string; total: number; [series: string]: number | string }

function DailySpend({
  points,
  ranked,
  featureLabel,
}: {
  points: DayPoint[]
  ranked: Bucket[]
  featureLabel: (key: string) => string
}) {
  const series = useMemo(() => ranked.slice(0, SERIES_COUNT).map((bucket) => bucket.key), [ranked])
  const colors = useMemo(() => {
    const free = PALETTE.filter((_, slot) => !series.includes(OWNED_SLOTS[slot] ?? ''))
    return Object.fromEntries(
      series.map((key) => {
        const owned = OWNED_SLOTS.indexOf(key)
        return [key, owned >= 0 ? PALETTE[owned] : free.shift()]
      }),
    ) as Record<string, string>
  }, [series])
  const hasOther = ranked.length > series.length
  const data = useMemo<ChartRow[]>(
    () =>
      points.map((point) => {
        const row: ChartRow = { date: point.date, label: dayLabel(point.date), total: Number(point.cost_usd) }
        for (const key of [...series, OTHER_KEY]) row[key] = 0
        for (const part of point.by_feature ?? []) {
          const key = series.includes(part.key) ? part.key : OTHER_KEY
          row[key] = Number(row[key]) + Number(part.cost_usd)
        }
        return row
      }),
    [points, series],
  )
  const name = (key: string) => (key === OTHER_KEY ? 'Everything else' : featureLabel(key))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Daily spend</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[280px] w-full">
          {data.length === 0 ? (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">No spend in these dates</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} barCategoryGap="18%" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID_COLOR} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={8} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={AXIS_TICK}
                  width={52}
                  tickFormatter={(value: number) => compactMoney.format(value)}
                />
                <Tooltip content={<DayTooltip name={name} />} cursor={{ fill: 'oklch(0.97 0 0)' }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                {series.map((key) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={name(key)}
                    stackId="spend"
                    fill={colors[key]}
                    stroke="#fff"
                    strokeWidth={1}
                    maxBarSize={40}
                    isAnimationActive={false}
                  />
                ))}
                {hasOther ? (
                  <Bar
                    dataKey={OTHER_KEY}
                    name={name(OTHER_KEY)}
                    stackId="spend"
                    fill={OTHER_COLOR}
                    stroke="#fff"
                    strokeWidth={1}
                    maxBarSize={40}
                    isAnimationActive={false}
                  />
                ) : null}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function DayTooltip({ active, payload, name }: TooltipProps<number, string> & { name: (key: string) => string }) {
  const row = active ? (payload?.[0]?.payload as ChartRow | undefined) : undefined
  if (!row) return null
  const parts = (payload ?? [])
    .filter((entry) => Number(entry.value) > 0)
    .sort((a, b) => Number(b.value) - Number(a.value))
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-medium">
        {dayLabel(row.date)} · {formatMoney(row.total)}
      </p>
      <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 tabular-nums">
        {parts.map((entry) => (
          <div key={String(entry.dataKey)} className="contents">
            <dt className="flex items-center gap-1.5 text-muted-foreground">
              <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} aria-hidden="true" />
              {name(String(entry.dataKey))}
            </dt>
            <dd className="text-right">{formatMoney(Number(entry.value))}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function BreakdownCard({
  title,
  noun,
  rows,
  selected,
  onSelect,
  className,
}: {
  title: string
  noun: string
  rows: Bucket[]
  selected?: string
  onSelect?: (key: string) => void
  className?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? rows : rows.slice(0, LIST_COUNT)
  const listId = `ai-costs-${noun}`
  return (
    <Card className={cn('gap-3', className)}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-3">
        {rows.length === 0 ? (
          <p className="px-3 py-4 text-sm text-muted-foreground">No spend in these dates</p>
        ) : (
          <ul id={listId} className="divide-y">
            {shown.map((row) => (
              <li key={row.key || row.label}>
                <BreakdownRow row={row} selected={selected === row.key} onSelect={onSelect} />
              </li>
            ))}
          </ul>
        )}
        {rows.length > LIST_COUNT ? (
          <FoldToggle
            expanded={expanded}
            total={rows.length}
            noun={noun}
            controls={listId}
            onToggle={() => setExpanded((open) => !open)}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}

function BreakdownRow({
  row,
  selected,
  onSelect,
}: {
  row: Bucket
  selected: boolean
  onSelect?: (key: string) => void
}) {
  const parts = row.features ?? []
  const rest = parts.slice(PART_COUNT)
  const body = (
    <>
      <span className="flex items-center justify-between gap-4">
        <span className="min-w-0">
          <span className="block truncate font-medium">{row.label}</span>
          {row.description ? (
            <span className="block text-xs text-muted-foreground">{row.description}</span>
          ) : null}
        </span>
        <span className="shrink-0 text-right tabular-nums">
          <span className="block font-medium">{formatMoney(row.cost_usd)}</span>
          <span className="block text-xs text-muted-foreground">{formatNumber(row.call_count, 0)} calls</span>
        </span>
      </span>
      {parts.length > 0 ? (
        <span className="mt-1.5 block space-y-0.5 text-xs text-muted-foreground">
          {parts.slice(0, PART_COUNT).map((part) => (
            <PartLine key={part.key} label={part.label} cost={part.cost_usd} />
          ))}
          {rest.length > 0 ? (
            <PartLine
              label={`${rest.length} more`}
              cost={rest.reduce((sum, part) => sum + Number(part.cost_usd), 0)}
            />
          ) : null}
        </span>
      ) : null}
    </>
  )
  const layout = 'block w-full rounded-md px-3 py-2 text-left text-sm'
  if (!onSelect) return <div className={layout}>{body}</div>
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(row.key)}
      className={cn(
        layout,
        'transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        selected && 'bg-muted',
      )}
    >
      {body}
    </button>
  )
}

function PartLine({ label, cost }: { label: string; cost: number | string }) {
  return (
    <span className="flex justify-between gap-4">
      <span className="truncate">{label}</span>
      <span className="shrink-0 tabular-nums">{formatMoney(cost)}</span>
    </span>
  )
}
